import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { StepInitStatus } from 'components/layout';
import { isCreativeId, useLocationParams } from 'hooks';
import { getCreatives, queryKeys as creativesQueryKeys } from 'features/creatives';
import { Network, TikTokAdType } from '../../../constants';
import { getAccounts } from '../api/getAccounts';
import { getAdSets } from '../api/getAdSets';
import { getCampaigns } from '../api/getCampaigns';
import queryKeys from '../api/queryKeys';
import { buildPacksFromCreativesByPack } from '../utils/buildPacksFromCreativesByPack';
import { idsFromSelection } from '../utils/idSelection';

interface DeepLinkInitializerProps {
  targetStep: number;
  onComplete: () => void;
  onReset: () => void;
  onStatusChange: (statuses: StepInitStatus[]) => void;
}

const STEP_LABELS = ['Ad Account', 'Creatives', 'Campaign', 'Ad sets', 'Ad settings'];

const CREATIVES_FORMAT_MISMATCH =
  'Creatives URL format is not valid for TikTok Ads Automation';

const DeepLinkInitializer = ({
  targetStep,
  onComplete,
  onReset,
  onStatusChange,
}: DeepLinkInitializerProps) => {
  const { watch, setValue } = useFormContext();
  const formApp = watch('app') as string | undefined;

  const {
    app: appParam,
    adAccount: adAccountParam,
    selectedCampaigns: selectedCampaignsParam,
    selectedAdSets: selectedAdSetsParam,
    creativesByPack,
    creativesFormat,
    resetLocationParamsToStart,
  } = useLocationParams();

  const resolvedApp = appParam ?? formApp;
  const creativesCompatible = creativesFormat !== 'flat';
  const urlCreativeIds = useMemo(() => {
    if (creativesFormat !== 'pack') return [];
    return [...new Set(creativesByPack.flatMap((group) => group.creatives).map(String))];
  }, [creativesByPack, creativesFormat]);
  const invalidCreativeIds = useMemo(
    () => urlCreativeIds.filter((id) => !isCreativeId(id)),
    [urlCreativeIds],
  );
  const uniqueCreativeIds = useMemo(
    () => urlCreativeIds.filter((id) => isCreativeId(id)),
    [urlCreativeIds],
  );
  const needsCreativeIdCheck = creativesCompatible && urlCreativeIds.length > 0;
  const needsCreativeListFetch = creativesCompatible && uniqueCreativeIds.length > 0;

  const creativesRequestParams = useMemo(
    () => ({
      app: resolvedApp ?? undefined,
      ad_type: TikTokAdType.SINGLE_VIDEO,
      network: Network.TIKTOK,
    }),
    [resolvedApp],
  );

  const creativesQuery = useQuery({
    queryKey: creativesQueryKeys.creatives(creativesRequestParams),
    queryFn: ({ signal }) => getCreatives({ params: creativesRequestParams }, signal),
    enabled: needsCreativeListFetch,
    staleTime: 0,
  });

  const knownCreativeIds = useMemo(() => {
    if (!creativesQuery.data) return undefined;
    return new Set(creativesQuery.data.map((row) => String(row.id)));
  }, [creativesQuery.data]);

  const creativesMissing = useMemo(() => {
    const missingFromList = knownCreativeIds
      ? uniqueCreativeIds.filter((id) => !knownCreativeIds.has(id))
      : [];
    return [...invalidCreativeIds, ...missingFromList];
  }, [invalidCreativeIds, knownCreativeIds, uniqueCreativeIds]);

  const step1Status: StepInitStatus = (() => {
    if (!creativesCompatible) return 'failed';
    if (!needsCreativeIdCheck) return 'success';
    if (!needsCreativeListFetch) {
      return creativesMissing.length > 0 ? 'failed' : 'success';
    }
    if (creativesQuery.isPending) return 'loading';
    if (creativesQuery.isError) return 'failed';
    if (creativesMissing.length > 0) return 'failed';
    return 'success';
  })();
  const step1Pass = step1Status === 'success';

  const needsAccountCheck = targetStep >= 0 && !!adAccountParam && step1Pass;
  const needsCampaignCheck = targetStep >= 2 && step1Pass;
  const needsAdSetCheck = targetStep >= 3 && step1Pass;

  const accountsQuery = useQuery({
    queryKey: [queryKeys.accounts, { app: resolvedApp, platform: 'WEB' }],
    queryFn: ({ signal }) =>
      getAccounts({ params: { app: resolvedApp, platform: 'WEB' } }, signal),
    enabled: needsAccountCheck,
    staleTime: 0,
  });

  const accountsMissing = useMemo<string[]>(() => {
    if (!adAccountParam || !accountsQuery.data) return [];
    return accountsQuery.data.some((account) => account.account_id === adAccountParam)
      ? []
      : [adAccountParam];
  }, [accountsQuery.data, adAccountParam]);

  const step0Status: StepInitStatus = (() => {
    if (!needsAccountCheck) return adAccountParam && !step1Pass ? 'idle' : 'success';
    if (accountsQuery.isPending) return 'loading';
    if (accountsQuery.isError) return 'failed';
    if (accountsMissing.length > 0) return 'failed';
    return 'success';
  })();
  const step0Pass = step0Status === 'success';

  const campaignIds = useMemo(
    () => idsFromSelection(selectedCampaignsParam),
    [selectedCampaignsParam],
  );

  const campaignsQuery = useQuery({
    queryKey: [queryKeys.campaigns, adAccountParam],
    queryFn: ({ signal }) =>
      getCampaigns({ params: { accountId: adAccountParam ?? '' } }, signal),
    enabled: needsCampaignCheck && step0Pass && !!adAccountParam,
    staleTime: 0,
  });

  const campaignsMissingIds = useMemo(() => {
    if (!campaignsQuery.data) return [];
    const knownIds = new Set(
      campaignsQuery.data.flatMap((row) =>
        [row.campaign_id, row.id].filter((id): id is string => typeof id === 'string'),
      ),
    );
    return campaignIds.filter((id) => !knownIds.has(id));
  }, [campaignIds, campaignsQuery.data]);

  const step2Status: StepInitStatus = (() => {
    if (!needsCampaignCheck) return 'idle';
    if (!step0Pass) return 'idle';
    if (campaignsQuery.isPending) return 'loading';
    if (campaignsQuery.isError) return 'failed';
    if (campaignsMissingIds.length > 0) return 'failed';
    return 'success';
  })();
  const step2Pass = !needsCampaignCheck || step2Status === 'success';

  const adSetsQuery = useQuery({
    queryKey: [queryKeys.adsets, adAccountParam, campaignIds],
    queryFn: ({ signal }) =>
      getAdSets(
        {
          params: {
            accountId: adAccountParam ?? '',
            selectedCampaigns: campaignIds,
          },
        },
        signal,
      ),
    enabled: needsAdSetCheck && step2Pass && campaignIds.length > 0 && !!adAccountParam,
    staleTime: 0,
  });

  const adSetIds = useMemo(
    () => idsFromSelection(selectedAdSetsParam),
    [selectedAdSetsParam],
  );

  const adSetsMissingIds = useMemo(() => {
    if (!adSetsQuery.data) return [];
    const knownIds = new Set(
      adSetsQuery.data.flatMap((campaign) =>
        campaign.ad_groups.map((group) => group.adgroup_id),
      ),
    );
    return adSetIds.filter((id) => !knownIds.has(id));
  }, [adSetIds, adSetsQuery.data]);

  const step3Status: StepInitStatus = (() => {
    if (!needsAdSetCheck) return 'idle';
    if (!step2Pass) return 'idle';
    if (campaignIds.length === 0) return 'success';
    if (adSetsQuery.isPending) return 'loading';
    if (adSetsQuery.isError) return 'failed';
    if (adSetsMissingIds.length > 0) return 'failed';
    return 'success';
  })();
  const step3Pass = !needsAdSetCheck || step3Status === 'success';

  const step4Status: StepInitStatus = needsAdSetCheck && step3Pass ? 'success' : 'idle';
  const step4Pass = !needsAdSetCheck || step4Status === 'success';

  const allPass = step0Pass && step1Pass && step2Pass && step3Pass && step4Pass;
  const anyFailed =
    step0Status === 'failed' ||
    step1Status === 'failed' ||
    step2Status === 'failed' ||
    step3Status === 'failed';

  const statusArray = useMemo<StepInitStatus[]>(
    () => [step0Status, step1Status, step2Status, step3Status, step4Status],
    [step0Status, step1Status, step2Status, step3Status, step4Status],
  );

  useEffect(() => {
    onStatusChange(statusArray);
  }, [statusArray, onStatusChange]);

  const hasCompleted = useRef(false);

  useEffect(() => {
    if (!allPass || hasCompleted.current) return;
    hasCompleted.current = true;

    if (needsCreativeIdCheck) {
      const knownIds = new Set(uniqueCreativeIds);
      const packs = buildPacksFromCreativesByPack(creativesByPack).map((pack) => ({
        ...pack,
        creatives: pack.creatives.filter(
          (id): id is string => typeof id === 'string' && knownIds.has(id),
        ),
      }));

      if (packs.length) {
        setValue('packsWithCreatives', packs);
        setValue('activePacksWithCreatives', 0);
        setValue(
          'selectedCreatives',
          (packs[0]?.creatives ?? []).filter((id): id is string => typeof id === 'string'),
        );
      }
    }

    if (needsCampaignCheck) {
      setValue('selectedCampaigns', campaignIds.filter((id) => !campaignsMissingIds.includes(id)));
    }

    if (needsAdSetCheck) {
      setValue('selectedAdGroups', adSetIds.filter((id) => !adSetsMissingIds.includes(id)));
    }

    onComplete();
  }, [
    allPass,
    needsCreativeIdCheck,
    uniqueCreativeIds,
    creativesByPack,
    needsCampaignCheck,
    needsAdSetCheck,
    campaignIds,
    campaignsMissingIds,
    adSetIds,
    adSetsMissingIds,
    setValue,
    onComplete,
  ]);

  const failedStepMessages = useMemo(() => {
    if (step1Status === 'failed') {
      if (!creativesCompatible) {
        return [{ label: STEP_LABELS[1], text: CREATIVES_FORMAT_MISMATCH }];
      }
      if (creativesMissing.length > 0) {
        return [{ label: STEP_LABELS[1], text: `Not found: ${creativesMissing.join(', ')}` }];
      }
      return [{ label: STEP_LABELS[1], text: 'Failed to load data' }];
    }

    const statuses = [step0Status, step2Status, step3Status];
    const missingByStep = [accountsMissing, campaignsMissingIds, adSetsMissingIds];
    const labels = [STEP_LABELS[0], STEP_LABELS[2], STEP_LABELS[3]];

    return statuses.flatMap((status, i) => {
      if (status !== 'failed') return [];
      const missingIds = missingByStep[i];
      if (missingIds.length > 0) {
        return [{ label: labels[i], text: `Not found: ${missingIds.join(', ')}` }];
      }
      return [{ label: labels[i], text: 'Failed to load data' }];
    });
  }, [
    step1Status,
    creativesCompatible,
    creativesMissing,
    step0Status,
    step2Status,
    step3Status,
    accountsMissing,
    campaignsMissingIds,
    adSetsMissingIds,
  ]);

  const handleStartFromBeginning = useCallback(() => {
    resetLocationParamsToStart();
    onReset();
  }, [onReset, resetLocationParamsToStart]);

  if (anyFailed) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            width: '100%',
            maxWidth: 600,
            textAlign: 'center',
          }}
        >
          {failedStepMessages.map(({ label, text }) => (
            <Typography key={label} color="error.main" variant="body1" sx={{ lineHeight: 3 / 2 }}>
              {text}
            </Typography>
          ))}
          <Button variant="outlined" color="error" size="small" onClick={handleStartFromBeginning}>
            Start from beginning
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <CircularProgress />
    </Box>
  );
};

export default DeepLinkInitializer;
